import { useMutation } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core';
import { useCallback } from 'react';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
    GridActionsCellItem,
    GridRowModes,
    GridRowParams,
    useGridApiContext,
} from '@mui/x-data-grid-premium';

import Api from '@barboxx/api';
import { BusinessLocker } from '@barboxx/models';

export function EditSaveAction({ id, row }: GridRowParams<BusinessLocker>) {
    const apiRef = useGridApiContext();
    const isInEditMode = apiRef.current.getRowMode(id) === GridRowModes.Edit;

    const handleEditClick = useCallback(() => {
        apiRef.current.startRowEditMode({
            id,
            fieldToFocus: 'priceTierId',
        });
    }, [apiRef, id]);

    const handleSaveClick = useCallback(() => {
        apiRef.current.stopRowEditMode({ id });
    }, [apiRef, id]);

    if (isInEditMode) {
        return (
            <GridActionsCellItem
                key="save"
                icon={<SaveIcon />}
                label="Save"
                onClick={handleSaveClick}
            />
        );
    }

    return (
        <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick}
            color="inherit"
        />
    );
}

export function CancelRelateAction({
    id,
    row,
    onDelete,
}: GridRowParams<BusinessLocker> & { onDelete: () => void }) {
    const apiRef = useGridApiContext();
    const dialog = useDialogs();

    const isInEditMode = apiRef.current.getRowMode(id) === GridRowModes.Edit;

    const mutateDeleteProductPriceTier = useMutation({
        mutationFn: async () => Api.BusinessLockers.removeLocker(row.id),
        mutationKey: ['businessLockerDelete', row.id],
        onSuccess: () => {
            apiRef.current.updateRows([{ id, _action: 'delete' }]);
            onDelete();
        },
    });

    const handleCancelClick = useCallback(() => {
        apiRef.current.stopRowEditMode({ id, ignoreModifications: true });
    }, [apiRef, id]);

    const handleRemoveClick = useCallback(async () => {
        const deleteConfirmed = await dialog.confirm('Delete Locker', {
            severity: 'error',
            okText: 'Delete',
        });

        if (!deleteConfirmed) {
            return;
        }
        return mutateDeleteProductPriceTier.mutateAsync();
    }, [dialog, mutateDeleteProductPriceTier]);

    if (isInEditMode) {
        return (
            <GridActionsCellItem
                key="cancel"
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick}
                color="inherit"
            />
        );
    }

    return (
        <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon color="error" />}
            label="Remove"
            onClick={handleRemoveClick}
            className="textPrimary"
        />
    );
}
